import React, { useMemo } from "react";

import { Space, Track } from "@highnote/server/src/core/entities";
import { ReactComponent as InfoSVG } from "App/common/icons-v2/info.svg";
import {
  ConnectedUserAvatar,
  USER_AVATAR_SIZE,
} from "App/common/UserAvatar/UserAvatar";
import { ButtonV2 } from "App/core/ButtonV2";
import { useAuth } from "./Auth";
import { EntityTable, EntityRowConfig } from "./EntityTable";
import { ColumnConfig } from "App/common/BaseTable";
import {
  useFormatSpaceToBaseEntityRow,
  useFormatTrackToBaseEntityRow,
} from "./EntityTable/ExpandableSpace/ExpandableSpaceRow";
import { PermissionTooltip } from "./PermissionTooltip";
import { SearchEntityInput } from "./SearchEntityInput";
import { useSearchContext } from "./useEntities/useSearchContext";
import { useTopLevelSpaces } from "./useEntities/useTopLevelSpaces";
import { possessiveName } from "./util";
import { THEME, ThemeProvider } from "App/common/ThemeProvider";

import styles from "./SearchEntityList.module.scss";

export const SearchEntityList = ({
  parentSpace,
  tableBaseProps,
  libraryRoot,
  hideSearch,
  defaultRows,
}: {
  parentSpace?: Space;
  tableBaseProps: {
    filter?: (row: EntityRowConfig) => boolean;
    sort?: (a: EntityRowConfig, b: EntityRowConfig) => number;
    onSelect?: (row: EntityRowConfig) => void;
    EmptyComponent?: React.FC;
    columns?: ColumnConfig[];
    disabled?: boolean;
    transformRows?: (rows: EntityRowConfig[]) => EntityRowConfig[];
    entireRowClickable?: boolean;
  };
  libraryRoot?: {
    hideLibraryRoot?: boolean;
    disableLibraryRoot?: boolean;
    onClick?: () => void;
  };
  hideSearch?: boolean;
  /**
   * Caller-provided default rows shown when there's no active search query.
   * When omitted, falls back to `topLevelSpaces` (the user's library) — the
   * existing behavior for Move-to and the Library-tab of EntitySearch. The
   * Highnote tab of EntitySearch passes Discover-derived rows here.
   */
  defaultRows?: EntityRowConfig[];
}) => {
  const { user } = useAuth();
  const { topLevelSpaces } = useTopLevelSpaces();
  const { formatSpaceToBaseEntityRow } = useFormatSpaceToBaseEntityRow();
  const { formatTrackToBaseEntityRow } = useFormatTrackToBaseEntityRow();
  const { searchResults } = useSearchContext();

  const spaceRows = useMemo(() => {
    if (searchResults?.spaces) {
      return (
        searchResults.spaces.map((space) => {
          return formatSpaceToBaseEntityRow({
            space: space as Space,
          });
        }) || []
      );
    }

    // No active search query — show caller-provided defaults if any,
    // otherwise fall back to the user's library.
    if (defaultRows) return defaultRows;

    return topLevelSpaces.map((topLevelSpace) => {
      return formatSpaceToBaseEntityRow({
        space: topLevelSpace,
      });
    });
  }, [parentSpace, topLevelSpaces, searchResults?.spaces, defaultRows]);

  const trackRows = useMemo(() => {
    return (
      searchResults?.tracks?.map((track) =>
        formatTrackToBaseEntityRow({
          track: track as Track,
        }),
      ) || []
    );
  }, [searchResults?.tracks]);

  const hideLibraryRoot = libraryRoot?.hideLibraryRoot || !!searchResults;

  // Compute rows once - no redundant processing
  const rows = useMemo(() => {
    let combinedRows = [...spaceRows, ...trackRows];
    if (tableBaseProps.filter) {
      combinedRows = combinedRows.filter(tableBaseProps.filter);
    }
    if (tableBaseProps.transformRows) {
      combinedRows = tableBaseProps.transformRows(combinedRows);
    }
    if (tableBaseProps.entireRowClickable) {
      combinedRows.forEach((row) => {
        row.entireRowClickable = tableBaseProps.entireRowClickable;
      });
    }
    // Only override onClick for rows that have onClick (i.e., are enabled)
    if (tableBaseProps.onSelect) {
      return combinedRows.map((row) =>
        row.onClick
          ? { ...row, onClick: () => tableBaseProps.onSelect!(row) }
          : row,
      );
    }
    return combinedRows;
  }, [
    spaceRows,
    trackRows,
    tableBaseProps.filter,
    tableBaseProps.transformRows,
    tableBaseProps.onSelect,
  ]);

  return (
    <>
      <div className={styles.root}>
        {!hideSearch && <SearchEntityInput />}
        {!hideLibraryRoot && (
          <div className={styles["library-root"]}>
            <PermissionTooltip
              hasPermission={!libraryRoot?.disableLibraryRoot}
              title="Only spaces can be added to the Library."
            >
              <ButtonV2
                className={styles["library-root-button"]}
                align="align-start"
                width="width-full"
                startIcon={
                  <ConnectedUserAvatar
                    size={USER_AVATAR_SIZE.XLARGE}
                    userId={user.id}
                  />
                }
                disabled={libraryRoot?.disableLibraryRoot}
                onClick={libraryRoot?.onClick}
              >
                <div className={styles["library-root-text"]}>
                  <span className={styles["library-root-text-title"]}>
                    {`${possessiveName(user.name)} Library`}
                  </span>
                  <span className={styles["library-root-text-subtitle"]}>
                    Root Space
                  </span>
                </div>
              </ButtonV2>
            </PermissionTooltip>
          </div>
        )}
      </div>
      <div className={styles["library-container"]}>
        <ThemeProvider theme={THEME.DARK}>
          <div className="highnote-entity-library">
            <EntityTable
              hideSelectionColumn
              rows={rows}
              columns={tableBaseProps.columns || []}
              sort={tableBaseProps.sort}
              onSelect={tableBaseProps.onSelect}
              EmptyComponent={
                tableBaseProps.EmptyComponent ||
                (() => (
                  <div className={styles["empty-search"]}>
                    <span>
                      <InfoSVG />
                    </span>
                    <span className={styles["empty-search-text"]}>
                      No results found
                    </span>
                  </div>
                ))
              }
              isLoading={tableBaseProps.disabled}
            />
          </div>
        </ThemeProvider>
      </div>
    </>
  );
};
